
import { Signal, signal } from '@angular/core';
import { debounce, FieldTree, max, min, required, SchemaPathTree } from '@angular/forms/signals';
import { SystemVoltageDto, VDto, VoltageAC1phDto, VoltageAC3phDto } from '../../../core/mceapi.client.generated';
import { appOpt, getNoneOptionDto, getSomeOptionDto } from '../../../core/mceapi.client.helpers';
import { match, Option } from '../../../shared/functions/functional';

export enum VoltageCase { DC = 'dc', AC1ph = 'ac1ph', AC3ph = 'ac3ph', TPN = 'tpn' }

export interface SystemVoltage {
  case: VoltageCase,
  v: number,
  u0: number,
  u: number,
  f: number;
}

export const systemVoltageCaseSelect = signal([
  { value: VoltageCase.DC, label: 'DC' },
  { value: VoltageCase.AC1ph, label: 'AC 1-Phase' },
  { value: VoltageCase.AC3ph, label: 'AC 3-Phase' },
  { value: VoltageCase.TPN, label: 'TPN (3ph+neutral)' }
]);


export function buildSystemVoltageSchema(path: SchemaPathTree<SystemVoltage>) {

  required(path.v, { message: `Voltage is required` });
  min(path.v, 0, { message: `Voltage must be positive` });
  max(path.v, 33000, { message: `Voltage must be less than or equal to 33 kV` });
  debounce(path.v, 500);

  required(path.u0, { message: `Voltage is required` });
  min(path.u0, 0, { message: `Voltage must be positive` });
  max(path.u0, 19500, { message: `Voltage must be less than or equal to 19 kV` });
  debounce(path.u0, 500);

  required(path.u, { message: `Voltage is required` });
  min(path.u, 0, { message: `Voltage must be positive` });
  max(path.u, 33000, { message: `Voltage must be less than or equal to 33 kV` });
  debounce(path.u, 500);

  required(path.f, { message: `Frequency is required` });
  min(path.f, 0, { message: `Frequency must be positive` });
  max(path.f, 500, { message: `Frequency must be less than or equal to 500 Hz` });

};

const getV = (vOpt: Option<VDto>) => match(() => 100, (x: VDto) => x.v ?? 100)(vOpt);
const getU0 = (u0Opt: Option<VoltageAC1phDto>) => match(() => 230, (x: VoltageAC1phDto) => x.uo ?? 230)(u0Opt);
const getU = (uOpt: Option<VoltageAC3phDto>) => match(() => 400, (x: VoltageAC3phDto) => x.u ?? 400)(uOpt);
const getF = (sv: SystemVoltageDto) => {
  switch (sv.case) {
    case VoltageCase.DC: return 50;
    case VoltageCase.AC1ph: return match(() => 50, (x: VoltageAC1phDto) => x.frequency ?? 50)(appOpt(sv.aC1ph ?? {}));
    case VoltageCase.AC3ph: return match(() => 50, (x: VoltageAC3phDto) => x.frequency ?? 50)(appOpt(sv.aC3ph ?? {}));
    case VoltageCase.TPN: return match(() => 50, (x: VoltageAC3phDto) => x.frequency ?? 50)(appOpt(sv.tpn ?? {}));
    default: return 50;
  }
};

export function createSystemVoltage(systemVoltage: SystemVoltageDto) {
  const voltageCase = systemVoltage.case;
  if (!Object.values(VoltageCase).includes(voltageCase as VoltageCase)) {
    throw new Error('System voltage DTO has an invalid case.');
  }

  return signal<SystemVoltage>({
    case: voltageCase as VoltageCase,
    v: getV(appOpt(systemVoltage.dc ?? {})),
    u0: getU0(appOpt(systemVoltage.aC1ph ?? {})),
    u: voltageCase === VoltageCase.AC3ph
      ? getU(appOpt(systemVoltage.aC3ph ?? {}))
      : getU(appOpt(systemVoltage.tpn ?? {})),
    f: getF(systemVoltage)
  });
}

export function createSystemVoltageDto(systemVoltage: FieldTree<SystemVoltage>): Signal<SystemVoltageDto> {
  let base = {
    case: systemVoltage.case().value(),
    dc: getNoneOptionDto<VDto>(),
    aC1ph: getNoneOptionDto<VoltageAC1phDto>(),
    aC3ph: getNoneOptionDto<VoltageAC3phDto>(),
    tpn: getNoneOptionDto<VoltageAC3phDto>()
  } as SystemVoltageDto;

  switch (base.case) {
    case VoltageCase.DC: return signal<SystemVoltageDto>({
      ...base, dc: getSomeOptionDto<VDto>({ v: systemVoltage.v().value() })
    });
    case VoltageCase.AC1ph: return signal<SystemVoltageDto>({
      ...base, aC1ph: getSomeOptionDto<VoltageAC1phDto>({ uo: systemVoltage.u0().value(), frequency: systemVoltage.f().value() })
    });
    case VoltageCase.AC3ph: return signal<SystemVoltageDto>({
      ...base, aC3ph: getSomeOptionDto<VoltageAC3phDto>({ u: systemVoltage.u().value(), frequency: systemVoltage.f().value() })
    });
    case VoltageCase.TPN: return signal<SystemVoltageDto>({
      ...base, tpn: getSomeOptionDto<VoltageAC3phDto>({ u: systemVoltage.u().value(), frequency: systemVoltage.f().value() })
    });
  }

  throw new Error(`Unsupported system voltage case '${base.case}'.`);
}

export function isDC(systemVoltage: FieldTree<SystemVoltage>) {
  return signal(systemVoltage.case().value() === VoltageCase.DC);
}
